import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Globe, Lock, BookOpen, Users, Trash2, Calendar,
  FileText, Tag, Edit2, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import AskAIPanel from "../components/AskAIPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", is_public: false });

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCollection = async () => {
    try {
      const resp = await axios.get(`${API}/collections/${id}`, { headers });
      setCollection(resp.data);
      setEditForm({
        name: resp.data.name,
        description: resp.data.description || "",
        is_public: resp.data.is_public,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error loading collection");
      navigate("/collections");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCollection(); }, [id]);

  const saveEdits = async () => {
    try {
      await axios.patch(`${API}/collections/${id}`, editForm, { headers });
      toast.success("Collection updated");
      setEditing(false);
      fetchCollection();
    } catch (err) { toast.error("Error saving"); }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/collections/${id}/items/${itemId}`, { headers });
      toast.success("Item removed");
      fetchCollection();
    } catch (err) { toast.error("Error removing item"); }
  };

  const toggleFollow = async () => {
    try {
      const resp = await axios.post(`${API}/collections/${id}/follow`, {}, { headers });
      toast.success(resp.data.following ? "Following!" : "Unfollowed");
      fetchCollection();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="text-center py-16 text-slate-500">Loading...</div>
    </div>
  );

  if (!collection) return null;

  const items = collection.items_data || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <Button variant="ghost" className="mb-4 text-slate-500" onClick={() => navigate("/collections")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Collections
        </Button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="collection-header">
          {editing ? (
            <div className="space-y-4">
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="text-lg font-semibold" data-testid="edit-name" />
              <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" rows={2} data-testid="edit-desc" />
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editForm.is_public} onChange={(e) => setEditForm({ ...editForm, is_public: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-orange-500" />
                <label className="text-sm text-slate-700">Public</label>
              </div>
              <div className="flex gap-2">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={saveEdits} data-testid="save-edit-btn"><Check className="w-4 h-4 mr-1" />Save</Button>
                <Button variant="outline" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="collection-title">
                    {collection.name}
                  </h1>
                  {collection.description && <p className="text-slate-500 mt-1">{collection.description}</p>}
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
                    {collection.is_public ? (
                      <span className="flex items-center gap-1 text-green-600"><Globe className="w-4 h-4" />Public</span>
                    ) : (
                      <span className="flex items-center gap-1"><Lock className="w-4 h-4" />Private</span>
                    )}
                    <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" />{items.length} items</span>
                    {collection.follower_count > 0 && (
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" />{collection.follower_count} followers</span>
                    )}
                    <span>by {collection.user_name || collection.user_email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {collection.is_owner ? (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-collection-btn">
                      <Edit2 className="w-4 h-4 mr-1" />Edit
                    </Button>
                  ) : collection.is_public && (
                    <Button variant={collection.is_following ? "outline" : "default"}
                      size="sm" onClick={toggleFollow}
                      className={collection.is_following ? "" : "bg-orange-500 hover:bg-orange-600 text-white"}
                      data-testid="follow-btn">
                      {collection.is_following ? "Unfollow" : "Follow"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Collection-wide Ask AI */}
        {user && items.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg mb-6 overflow-hidden" data-testid="collection-ask-ai-card">
            <AskAIPanel collectionId={id} />
          </div>
        )}

        {/* Items */}
        {items.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">This collection is empty</p>
            <p className="text-sm text-slate-400 mt-1">Browse items and use "Save to Collection" to add them here</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="collection-items">
            {items.map((item) => (
              <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 transition-all cursor-pointer group flex items-start justify-between"
                onClick={() => navigate(`/items/${item.id}`)} data-testid={`item-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors" style={{ fontFamily: "Fraunces, serif" }}>
                    {item.title}
                  </h3>
                  {item.authors?.length > 0 && <p className="text-sm text-slate-500 mt-0.5">{item.authors.join(", ")}</p>}
                  {item.abstract && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{item.abstract}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {item.year && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"><Calendar className="w-3 h-3" />{item.year}</span>}
                    {item.language && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">{item.language}</span>}
                    {item.file_type && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"><FileText className="w-3 h-3" />{item.file_type}</span>}
                    {item.tags?.slice(0, 3).map((t) => (
                      <span key={t} className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded"><Tag className="w-3 h-3" />{t}</span>
                    ))}
                    {item.price > 0 && <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded font-medium">${item.price.toFixed(2)}</span>}
                  </div>
                </div>
                {collection.is_owner && (
                  <Button variant="ghost" size="sm" className="shrink-0 opacity-0 group-hover:opacity-100 text-red-500"
                    onClick={(e) => { e.stopPropagation(); removeItem(item.id); }} data-testid={`remove-item-${item.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDetailPage;
